import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Chip,
  Tooltip,
  IconButton,
  Autocomplete
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp, getDocs, query, where } from 'firebase/firestore';
import { Info as InfoIcon, Delete as DeleteIcon } from '@mui/icons-material';

const AddClient = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();

  const [newClient, setNewClient] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    status: 'new',
    address: '',
    city: '',
    profession: '',
    company: '',
    turnover: '', // Nowe pole
    photoURL: '',
    socialMedia: { instagram: '', facebook: '', tiktok: '', other: '' },
    preferences: { ringSize: '', watchBrands: '', goldColor: '', other: '' },
    importantDates: { birthday: '', nameDay: '', anniversary: '' },
    notes: '',
    purchaseHistory: [], // Nowa tablica
    relations: [] // Nowa tablica dla powiązań
  });

  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [historyEntry, setHistoryEntry] = useState({ date: '', description: '', amount: '' });
  const [clients, setClients] = useState([]);
  const [relation, setRelation] = useState({ client: null, type: '' });

  const relationTypes = [
    'Mąż', 'Żona', 'Syn', 'Córka', 'Partner', 'Partnerka', 'Kolega', 'Koleżanka',
    'Narzeczony', 'Narzeczona', 'Kochanek', 'Kochanka', 'Mama', 'Tata', 'Brat', 'Siostra', 'Inne'
  ];

  useEffect(() => {
    const fetchClients = async () => {
      if (!currentUser) return;
      try {
        let q;
        if (isAdmin) {
          q = query(collection(db, 'clients'));
        } else {
          q = query(collection(db, 'clients'), where('assigned_to', '==', currentUser.uid));
        }
        const querySnapshot = await getDocs(q);
        const clientsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setClients(clientsData);
      } catch (error) {
        console.error("Błąd podczas pobierania klientów:", error);
        setSnackbar({ open: true, message: 'Nie udało się załadować listy klientów do powiązań.', severity: 'error' });
      }
    };
    fetchClients();
  }, [currentUser, isAdmin]);

  const handleNewClientChange = (e) => {
    const { name, value } = e.target;
    const keys = name.split('.');

    if (keys.length > 1) {
      setNewClient(prev => ({
        ...prev,
        [keys[0]]: {
          ...prev[keys[0]],
          [keys[1]]: value
        }
      }));
    } else {
      setNewClient(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.firstName || !newClient.lastName) {
      setSnackbar({ open: true, message: 'Imię i nazwisko są wymagane.', severity: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const clientData = {
        ...newClient,
        created_at: serverTimestamp(),
        last_updated_at: serverTimestamp(),
        created_by: currentUser.uid,
        assigned_to: currentUser.uid,
        total_spent: 0,
      };

      await addDoc(collection(db, 'clients'), clientData);

      setSnackbar({ open: true, message: 'Klient został dodany pomyślnie!', severity: 'success' });
      
      // Opcjonalnie: przekieruj po dodaniu klienta
      setTimeout(() => navigate('/clients'), 2000);

    } catch (error) {
      console.error("Błąd podczas dodawania klienta:", error);
      setSnackbar({ open: true, message: `Błąd: ${error.message}`, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleHistoryEntryChange = (e) => {
    const { name, value } = e.target;
    setHistoryEntry(prev => ({ ...prev, [name]: value }));
  };

  const handleAddHistoryEntry = () => {
    if (!historyEntry.description || !historyEntry.amount) {
      setSnackbar({ open: true, message: 'Opis i kwota w historii zakupów są wymagane.', severity: 'warning' });
      return;
    }
    setNewClient(prev => ({
      ...prev,
      purchaseHistory: [...prev.purchaseHistory, historyEntry]
    }));
    setHistoryEntry({ date: '', description: '', amount: '' });
  };

  const handleRemoveHistoryEntry = (index) => {
    setNewClient(prev => ({
      ...prev,
      purchaseHistory: prev.purchaseHistory.filter((_, i) => i !== index)
    }));
  };

  const handleAddRelation = () => {
    if (!relation.client || !relation.type) {
      setSnackbar({ open: true, message: 'Wybierz klienta i typ relacji.', severity: 'warning' });
      return;
    }
    setNewClient(prev => ({
      ...prev,
      relations: [...prev.relations, { clientId: relation.client.id, clientName: `${relation.client.firstName} ${relation.client.lastName}`, type: relation.type }]
    }));
    setRelation({ client: null, type: '' });
  };

  const handleRemoveRelation = (index) => {
    setNewClient(prev => ({
      ...prev,
      relations: prev.relations.filter((_, i) => i !== index)
    }));
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Paper sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h4" gutterBottom>Nowy Klient</Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
          Dodaj nowego klienta do bazy danych CRM. Pola oznaczone gwiazdką (*) są wymagane.
        </Typography>

        <Grid container spacing={4}>
          {/* SEKCJA DANE PODSTAWOWE */}
          <Grid item xs={12}>
            <Divider><Chip label="Dane Podstawowe" /></Divider>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label="Imię" name="firstName" value={newClient.firstName} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label="Nazwisko" name="lastName" value={newClient.lastName} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Email" name="email" type="email" value={newClient.email} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Telefon" name="phone" value={newClient.phone} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status Klienta</InputLabel>
              <Select name="status" value={newClient.status} onChange={handleNewClientChange} label="Status Klienta">
                <MenuItem value="new">Nowy</MenuItem>
                <MenuItem value="active">Aktywny</MenuItem>
                <MenuItem value="vip">VIP</MenuItem>
                <MenuItem value="inactive">Nieaktywny</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* SEKCJA ADRESOWA */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Divider><Chip label="Dane Adresowe" /></Divider>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Adres" name="address" value={newClient.address} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Miasto" name="city" value={newClient.city} onChange={handleNewClientChange} />
          </Grid>

          {/* SEKCJA DANE ZAWODOWE */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Divider><Chip label="Dane Zawodowe" /></Divider>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Wykonywany zawód" name="profession" value={newClient.profession} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Firma" name="company" value={newClient.company} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Obroty (informacje)" name="turnover" value={newClient.turnover} onChange={handleNewClientChange} />
          </Grid>

          {/* SEKCJA ZDJĘCIE */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Divider><Chip label="Zdjęcie Profilowe" /></Divider>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="URL do zdjęcia" name="photoURL" value={newClient.photoURL} onChange={handleNewClientChange} helperText="W przyszłości będzie tu możliwość dodawania zdjęcia z dysku." />
          </Grid>

          {/* SEKCJA SOCIAL MEDIA */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Divider><Chip label="Social Media" /></Divider>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Instagram" name="socialMedia.instagram" value={newClient.socialMedia.instagram} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Facebook" name="socialMedia.facebook" value={newClient.socialMedia.facebook} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="TikTok" name="socialMedia.tiktok" value={newClient.socialMedia.tiktok} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Inne Social Media" name="socialMedia.other" value={newClient.socialMedia.other} onChange={handleNewClientChange} />
          </Grid>

          {/* SEKCJA PREFERENCJE I ROZMIARY */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Divider><Chip label="Preferencje i Rozmiary" /></Divider>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Rozmiar pierścionka" name="preferences.ringSize" value={newClient.preferences.ringSize} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Ulubione marki zegarków" name="preferences.watchBrands" value={newClient.preferences.watchBrands} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Preferowany kolor złota</InputLabel>
              <Select name="preferences.goldColor" value={newClient.preferences.goldColor} onChange={handleNewClientChange} label="Preferowany kolor złota">
                <MenuItem value="">Brak preferencji</MenuItem>
                <MenuItem value="yellow">Żółte</MenuItem>
                <MenuItem value="white">Białe</MenuItem>
                <MenuItem value="rose">Różowe</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Inne preferencje" name="preferences.other" value={newClient.preferences.other} onChange={handleNewClientChange} />
          </Grid>

          {/* SEKCJA WAŻNE DATY */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Divider><Chip label="Ważne Daty" /></Divider>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Urodziny" name="importantDates.birthday" type="date" InputLabelProps={{ shrink: true }} value={newClient.importantDates.birthday} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Imieniny" name="importantDates.nameDay" type="date" InputLabelProps={{ shrink: true }} value={newClient.importantDates.nameDay} onChange={handleNewClientChange} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Rocznica" name="importantDates.anniversary" type="date" InputLabelProps={{ shrink: true }} value={newClient.importantDates.anniversary} onChange={handleNewClientChange} />
          </Grid>

          {/* SEKCJA NOTATKI */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Divider><Chip label="Notatki" /></Divider>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Notatki o kliencie" name="notes" multiline rows={4} value={newClient.notes} onChange={handleNewClientChange} />
          </Grid>

          {/* SEKCJA HISTORIA ZAKUPÓW */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Divider><Chip label="Historia Zakupów (bez ID produktu)" /></Divider>
          </Grid>
          {newClient.purchaseHistory.map((entry, index) => (
            <Grid item xs={12} key={index}>
              <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'action.hover' }}>
                <Typography variant="body2" sx={{ flex: 1 }}><strong>Data:</strong> {entry.date || 'Brak'}</Typography>
                <Typography variant="body2" sx={{ flex: 2 }}><strong>Opis:</strong> {entry.description}</Typography>
                <Typography variant="body2" sx={{ flex: 1 }}><strong>Kwota:</strong> {entry.amount} PLN</Typography>
                <IconButton onClick={() => handleRemoveHistoryEntry(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Data zakupu" name="date" type="date" InputLabelProps={{ shrink: true }} value={historyEntry.date} onChange={handleHistoryEntryChange} />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField fullWidth label="Opis produktu/usługi" name="description" value={historyEntry.description} onChange={handleHistoryEntryChange} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth label="Kwota" name="amount" type="number" value={historyEntry.amount} onChange={handleHistoryEntryChange} />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={handleAddHistoryEntry} sx={{ height: '100%' }}>Dodaj wpis</Button>
          </Grid>

          {/* SEKCJA POWIĄZANIA */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Divider><Chip label="Powiązania z innymi klientami" /></Divider>
          </Grid>
          {newClient.relations.map((rel, index) => (
            <Grid item xs={12} key={index}>
              <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'action.hover' }}>
                <Typography variant="body2" sx={{ flex: 2 }}><strong>Klient:</strong> {rel.clientName}</Typography>
                <Typography variant="body2" sx={{ flex: 1 }}><strong>Relacja:</strong> {rel.type}</Typography>
                <IconButton onClick={() => handleRemoveRelation(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={clients}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email || 'brak email'})`}
              value={relation.client}
              onChange={(event, newValue) => {
                setRelation(prev => ({ ...prev, client: newValue }));
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  {`${option.firstName} ${option.lastName} (${option.email || 'brak email'})`}
                </Box>
              )}
              renderInput={(params) => <TextField {...params} label="Wyszukaj klienta do powiązania" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Typ relacji</InputLabel>
              <Select
                value={relation.type}
                label="Typ relacji"
                onChange={(e) => setRelation(prev => ({ ...prev, type: e.target.value }))}
              >
                {relationTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={handleAddRelation} sx={{ height: '100%' }}>Dodaj powiązanie</Button>
          </Grid>

          {/* PRZYCISK ZAPISU */}
          <Grid item xs={12} sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleCreateClient}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : 'Dodaj Klienta'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddClient;
